import classNames from "classnames";
import { Popover } from "../ui/Popover";
import { Segmented } from "../ui/Segmented";
import {
  FILTER_FIELDS,
  OPS_BY_TYPE,
  OP_LABELS,
  enumOptions,
  filterSentence,
  type FilterCondition,
  type FilterField,
  type FilterOp,
  type GroupField,
  type RowProp,
  type SavedView,
  type SortField,
  type SortRule,
  type ViewLayout,
} from "../../lib/viewEngine";

const LAYOUTS: { value: ViewLayout; label: string }[] = [
  { value: "list", label: "Liste" },
  { value: "board", label: "Board" },
  { value: "calendar", label: "Calendrier" },
  { value: "gallery", label: "Galerie" },
  { value: "timeline", label: "Timeline" },
];

const GROUPS: { value: GroupField; label: string }[] = [
  { value: "none", label: "Aucun" },
  { value: "status", label: "Statut" },
  { value: "priority", label: "Priorité" },
  { value: "list", label: "Liste" },
  { value: "assignee", label: "Assigné" },
  { value: "due", label: "Échéance" },
];

const SORT_FIELDS: { value: SortField; label: string }[] = [
  { value: "due", label: "Échéance" },
  { value: "priority", label: "Priorité" },
  { value: "status", label: "Statut" },
  { value: "title", label: "Titre" },
];

const PROPS: { value: RowProp; label: string }[] = [
  { value: "status", label: "Statut" },
  { value: "priority", label: "Priorité" },
  { value: "due", label: "Échéance" },
  { value: "list", label: "Liste" },
  { value: "project", label: "Projet" },
  { value: "assignees", label: "Assignés" },
  { value: "tags", label: "Tags" },
  { value: "subtasks", label: "Sous-tâches" },
];

const selectStyle = {
  font: "inherit",
  fontSize: 12,
  border: "1px solid var(--border-3)",
  borderRadius: 6,
  padding: "2px 6px",
  background: "var(--bg)",
} as const;

interface Props {
  view: SavedView;
  onPatch: (patch: Partial<SavedView>) => void;
  /** Noms de listes pour les valeurs enum du filtre. */
  listNames: string[];
  /** Layouts réellement disponibles sur l'écran hôte. */
  layouts?: ViewLayout[];
}

/** Barre d'outils de vue — Disposition / Grouper / Trier / Filtrer / Propriétés.
 * ponytail: réordonnancement des tris par flèches ↑↓ (pas de D&D) — même
 * capacité, moins de code. */
export function ViewToolbar({ view, onPatch, listNames, layouts }: Props) {
  const layoutOptions = layouts
    ? LAYOUTS.filter((l) => layouts.includes(l.value))
    : LAYOUTS;

  const patchSort = (i: number, patch: Partial<SortRule>) => {
    const sort = view.sort.map((r, j) => (j === i ? { ...r, ...patch } : r));
    onPatch({ sort });
  };

  const moveSort = (i: number, dir: -1 | 1) => {
    const sort = [...view.sort];
    const j = i + dir;
    if (j < 0 || j >= sort.length) return;
    const a = sort[i]!;
    sort[i] = sort[j]!;
    sort[j] = a;
    onPatch({ sort });
  };

  const patchCondition = (i: number, patch: Partial<FilterCondition>) => {
    const conditions = view.filter.conditions.map((c, j) =>
      j === i ? { ...c, ...patch } : c,
    );
    onPatch({ filter: { ...view.filter, conditions } });
  };

  const conditionRow = (c: FilterCondition, i: number) => {
    const type = FILTER_FIELDS[c.field].type;
    const ops = OPS_BY_TYPE[type];
    const needsValue = !["empty", "not_empty", "overdue", "this_week"].includes(
      c.op,
    );
    return (
      <div key={i} className="filter-row">
        <select
          style={selectStyle}
          value={c.field}
          aria-label="Propriété"
          onChange={(e) => {
            const field = e.target.value as FilterField;
            const firstOp = OPS_BY_TYPE[FILTER_FIELDS[field].type][0]!;
            patchCondition(i, { field, op: firstOp, value: undefined });
          }}
        >
          {Object.entries(FILTER_FIELDS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <select
          style={selectStyle}
          value={c.op}
          aria-label="Opérateur"
          onChange={(e) =>
            patchCondition(i, { op: e.target.value as FilterOp })
          }
        >
          {ops.map((op) => (
            <option key={op} value={op}>
              {OP_LABELS[op]}
            </option>
          ))}
        </select>
        {needsValue &&
          (type === "enum" ? (
            <select
              style={selectStyle}
              value={c.value ?? ""}
              aria-label="Valeur"
              onChange={(e) => patchCondition(i, { value: e.target.value })}
            >
              <option value="">—</option>
              {enumOptions(c.field, listNames).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              style={{
                ...selectStyle,
                width: type === "date" ? undefined : 90,
              }}
              type={type === "date" ? "date" : "text"}
              value={c.value ?? ""}
              aria-label="Valeur"
              onChange={(e) => patchCondition(i, { value: e.target.value })}
            />
          ))}
        <button
          className="icon-btn"
          aria-label="Retirer la condition"
          onClick={() =>
            onPatch({
              filter: {
                ...view.filter,
                conditions: view.filter.conditions.filter((_, j) => j !== i),
              },
            })
          }
        >
          ×
        </button>
      </div>
    );
  };

  const nFilters = view.filter.conditions.length;

  return (
    <div className="view-toolbar">
      <Popover trigger={<>Disposition</>} triggerClassName="toolbar-btn">
        <div className="toolbar-panel-title">Disposition</div>
        <Segmented
          options={layoutOptions}
          value={view.layout}
          onChange={(layout) => onPatch({ layout })}
          ariaLabel="Disposition"
        />
      </Popover>

      <Popover trigger={<>Grouper</>} triggerClassName="toolbar-btn">
        <div className="toolbar-panel-title">Grouper par</div>
        <div className="chip-picker">
          {GROUPS.map((g) => (
            <button
              key={g.value}
              type="button"
              className={classNames("chip", "chip-choice", {
                active: view.groupBy === g.value,
              })}
              style={
                view.groupBy === g.value
                  ? { color: "var(--accent)", background: "var(--accent-wash)" }
                  : undefined
              }
              onClick={() => onPatch({ groupBy: g.value })}
            >
              {g.label}
            </button>
          ))}
        </div>
      </Popover>

      <Popover
        trigger={<>Trier{view.sort.length ? ` · ${view.sort.length}` : ""}</>}
        triggerClassName="toolbar-btn"
      >
        <div className="toolbar-panel-title">Tri multi-critères</div>
        {view.sort.map((r, i) => (
          <div key={i} className="filter-row">
            <select
              style={selectStyle}
              value={r.field}
              aria-label="Champ de tri"
              onChange={(e) =>
                patchSort(i, { field: e.target.value as SortField })
              }
            >
              {SORT_FIELDS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <button
              className="btn"
              style={{ padding: "2px 8px" }}
              onClick={() =>
                patchSort(i, { dir: r.dir === "asc" ? "desc" : "asc" })
              }
              aria-label="Inverser le sens"
            >
              {r.dir === "asc" ? "↑" : "↓"}
            </button>
            {i === 0 && <span className="tag">principal</span>}
            <span style={{ marginLeft: "auto", display: "inline-flex" }}>
              <button
                className="icon-btn"
                aria-label="Monter"
                disabled={i === 0}
                onClick={() => moveSort(i, -1)}
              >
                ▲
              </button>
              <button
                className="icon-btn"
                aria-label="Descendre"
                disabled={i === view.sort.length - 1}
                onClick={() => moveSort(i, 1)}
              >
                ▼
              </button>
              <button
                className="icon-btn"
                aria-label="Retirer le tri"
                onClick={() =>
                  onPatch({ sort: view.sort.filter((_, j) => j !== i) })
                }
              >
                ×
              </button>
            </span>
          </div>
        ))}
        <button
          className="btn"
          style={{ marginTop: 6 }}
          onClick={() =>
            onPatch({ sort: [...view.sort, { field: "title", dir: "asc" }] })
          }
        >
          + Ajouter un tri
        </button>
        <div className="toolbar-hint">
          Tri de stabilisation auto : Titre A→Z
        </div>
      </Popover>

      <Popover
        trigger={<>Filtrer{nFilters ? ` · ${nFilters}` : ""}</>}
        triggerClassName="toolbar-btn"
      >
        <div className="toolbar-panel-title">
          Filtres
          <span style={{ marginLeft: 8 }}>
            <Segmented
              options={[
                { value: "and", label: "et" },
                { value: "or", label: "ou" },
              ]}
              value={view.filter.operator}
              onChange={(operator) =>
                onPatch({ filter: { ...view.filter, operator } })
              }
              ariaLabel="Connecteur logique"
            />
          </span>
        </div>
        {view.filter.conditions.map(conditionRow)}
        <button
          className="btn"
          style={{ marginTop: 6 }}
          onClick={() =>
            onPatch({
              filter: {
                ...view.filter,
                conditions: [
                  ...view.filter.conditions,
                  { field: "status", op: "is", value: "todo" },
                ],
              },
            })
          }
        >
          + Ajouter une condition
        </button>
        <div className="toolbar-hint">{filterSentence(view.filter)}</div>
      </Popover>

      <Popover trigger={<>Propriétés</>} triggerClassName="toolbar-btn">
        <div className="toolbar-panel-title">Propriétés affichées</div>
        <div className="chip-picker">
          {PROPS.map((p) => {
            const visible = !view.hidden.includes(p.value);
            return (
              <button
                key={p.value}
                type="button"
                className={classNames("chip", "chip-choice", {
                  active: visible,
                })}
                style={
                  visible
                    ? {
                        color: "var(--accent)",
                        background: "var(--accent-wash)",
                      }
                    : undefined
                }
                aria-pressed={visible}
                onClick={() =>
                  onPatch({
                    hidden: visible
                      ? [...view.hidden, p.value]
                      : view.hidden.filter((h) => h !== p.value),
                  })
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </Popover>
    </div>
  );
}
